import { useEffect } from 'react';

import { add } from 'date-fns';
import lStorage from 'local-storage-fallback';
import { parse } from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

import { toaster } from '@m/components-ui/toaster';
import { handleQRCode, parseQRDelegation } from '@m/state/qrcode';
import { DefaultOnboardingRoutes, ProtectedRoutes } from 'apps/customer/routes/paths';
import { setFromSms, setPromoShortCode, TN_SMS_DISCOUNT_CODE } from 'apps/customer/state/promo';
import {
  setPromptUserSignIn,
  setSignInLandingPage,
  setSignInLandingPageState,
} from 'apps/customer/state/route';
import {
  getUser,
  logout,
  parseInvitationCode,
  parseEnterpriseInviteToken,
  setTokenAndUserInfoForEnterpriseInvitation,
  parseMemberReferralInvitationCode,
} from 'apps/customer/state/user';
import {
  customerApplyValidation,
  fetchCustomerVisits,
  setQRValidation,
  setDoorOpenRequest,
  setPendingValidationTerms,
} from 'apps/customer/state/visit';
import {
  SCANNED_NATIVE,
  trackValidationResponse,
  trackValidationScan,
} from 'apps/customer/utils/validationHeap';

import { KioskValidationService } from '../services/KioskValidationService';
import { setIndividualSubscriptionInvitationId } from '../state/membership';
import { setPendingPassValidation, setScannedDiscountUuid } from '../state/passes';
import { captureQAOnboardingOverride } from '../utils/qaOnboardingOverride';

/**
 * Initializes the customer app state,
 * by initiatied requests based off of the url and fetching the user
 *
 * This hook checks:
 * - If coming from enterprise invite link, parse code and fetch enterprise user info
 * - If coming from an invitation link, parse code and get user back
 * - If scanning a join QR, ask BE to provide the tasks (todoItems) and user if available
 * - If referrer is mpolis.io, set the initial landing page based off referrer pathname
 * - If scanning a validation QR, store the validationUUID so it can be applied after signup/signin
 * - If param access is present it will save the value to open a pedestrian door (customer scanned qr code)
 */
export function useUserInitialization() {
  const history = useHistory();
  const location = useLocation();

  // If user is already logged in and they try to pass an invitation code, they will be logged out
  // We want to first check if the invitation is a member referral, as that one results in a different API call
  // If an invite is passed then we will always display the login or welcome
  const parseInviteCodeAndLogoutIfFailed = async (invitationCode: string) => {
    const isMemberReferral = !!invitationCode.match(/^MR-.*$/);

    const memberReferralInvitationCode = invitationCode.slice(3);
    const response = isMemberReferral
      ? await parseMemberReferralInvitationCode({ invitationCode: memberReferralInvitationCode })
      : await parseInvitationCode({ invitationCode });
    if (!response.success) {
      logout();
      toaster.create({ title: 'No valid invitation found!', type: 'error' });
    }
  };

  const vehicleSettingsInitialPathCheck = (initalPath: string) => {
    if (initalPath.match(/^\/vehicle\/[0-9A-Za-z]+\/drivers$/)) return true;
    if (initalPath.match(/^\/vehicle\/[0-9A-Za-z]+$/)) return true;
    if (initalPath.match(/^\/vehicle\/?$/)) return true;
    return false;
  };

  useEffect(() => {
    const { pathname, search } = location;

    /**
     * Calls getUser() and redirects on unauthenticated response.
     * Pass a path string to always replace to that path,
     * or a callback for custom redirect logic.
     */
    const fetchUserAndRedirect = (redirect: string | (() => void)) => {
      getUser()
        .then((res) => {
          if (!res.success) {
            if (typeof redirect === 'string') {
              history.replace(redirect);
            } else {
              redirect();
            }
          }
        })
        .catch(() => {
          history.replace(DefaultOnboardingRoutes.SignIn);
        });
    };
    const {
      invitationCode,
      validation, // example: welcome/?validation=05d91590-ac70-446e-8ea9-103b7acb265d
      path,
      access,
      ent_token: entToken,
      token,
      firstName,
      lastName,
      employeeId,
      email,
      subInviteId,
      code,
      utm_campaign,
      utm_source,
    } = parse(search) as {
      access: string | undefined;
      code: string | undefined;
      email: string | undefined;
      employeeId: string | undefined;
      ent_token: string | undefined;
      firstName: string | undefined;
      invitationCode: string | undefined;
      lastName: string | undefined;
      path: string | undefined;
      prompt: string | undefined;
      subInviteId: string | undefined;
      token: string | undefined;
      utm_campaign: string | undefined;
      utm_source: string | undefined;
      validation: string | undefined;
    };

    captureQAOnboardingOverride(search);

    if (access) {
      setDoorOpenRequest(access);
    }

    // LANDS ON: app[env].metropolis.io/welcome?invitationCode=:uuid
    // TAKES TO: first time users - /welcome sign-up flow || existing user - /visit via auto login
    const gotHereByInvitationCode =
      invitationCode && typeof invitationCode === 'string' && pathname === '/welcome';

    // LANDS ON: app[env].metropolis.io/visit/start/:uuid
    // TAKES TO: /onboard
    const gotHereByStartVisitQRCode = pathname.match(/\/visit\/start\/[a-zA-Z0-9-]+/);

    // LANDS ON: app[env].metropolis.io/join/:uuid
    // TAKES TO: /onboard
    const gotHereByJoinQRCode = pathname.match(/\/join\/[a-zA-Z0-9-]+/);

    // LANDS ON: app[env].metropolis.io/?validation=:uuid
    // TAKES TO: /onboard
    const gotHereByValidationQRCode = validation;

    // LANDS ON: app[env].metropolis.io/login?ent_token=:uuid
    // TAKES TO: first time users - /sign-in || existing user - /membership
    const gotHereByEnterpriseInvitation = !!entToken;

    const gotHereByEnterpriseInvitationOnboardingToken = !!token && !!employeeId;

    // LANDS ON: app[env].metropolis.io/welcome?subInviteId=:uuid || id
    // TAKES TO: first time users - /welcome sign-up flow || existing user - /visit via auto login
    const gotHereByIndividualSubscriptionInvitationLink = !!subInviteId;

    // LANDS ON: app[env].metropolis.io/kiosk-validation?code=SECRET
    // TAKES TO: /onboard (via express onboarding after bootstrap)
    const gotHereByKioskValidationLink =
      pathname === '/kiosk-validation' && typeof code === 'string' && code.length > 0;

    const getUserandSetEnterpriseInfo = async () => {
      await getUser();
      await setTokenAndUserInfoForEnterpriseInvitation({
        enterpriseUser: {
          token: token as string,
          firstName: firstName as string,
          lastName: lastName as string,
          employeeId: employeeId as string,
          email: email as string,
        },
      });
    };

    if (gotHereByEnterpriseInvitation) {
      parseEnterpriseInviteToken({ token: entToken });
    } else if (gotHereByInvitationCode) {
      parseInviteCodeAndLogoutIfFailed(invitationCode);
    } else if (gotHereByStartVisitQRCode) {
      const uuid = pathname.split('/')[3];
      handleQRCode({ uuid });
    } else if (gotHereByJoinQRCode) {
      const uuid = pathname.split('/')[2];
      handleQRCode({ uuid });
    } else if (gotHereByValidationQRCode) {
      trackValidationScan({
        validationScanSource: SCANNED_NATIVE,
        pendingValidationUuid: validation,
      });

      customerApplyValidation({
        validationUUID: validation,
      }).then((response) => {
        setPendingValidationTerms(response.data?.validation?.validationTerms ?? null);

        const isDiscountPassValidation =
          response.success &&
          response.data?.validation &&
          response.data.validation.productType === 'pass';

        if (isDiscountPassValidation) {
          setScannedDiscountUuid({
            discountUuid: validation,
          });

          if (response.data?.validation) {
            setPendingPassValidation({
              ...response.data.validation,
              siteUuids: response.data.validationSiteUuids,
              validationQrUuid: validation,
            });
          }
          setSignInLandingPageState({ isPurchasingPass: true });
        }

        if (!response.success || (response.success && response.data?.transaction)) {
          // validation successfully applied if we receive a transaction from the API
          // if we receive a txn, then validation is successfully applied and user is logged in
          if (response.success && response.data?.transaction && response.data?.validation) {
            const {
              transaction: { id: visitId, siteId },
              validation: { id: validationId },
            } = response.data;
            trackValidationResponse({
              appliedValidationSuccess: true,
              appliedValidationUuid: validation,
              attemptedValidationId: validationId,
              appliedValidationTransactionId: visitId || 'null',
              appliedValidationSiteId: siteId || 'null',
              isParkingPassValidation: isDiscountPassValidation,
            });
          } else {
            // if this request fails, then user is logged in
            // fetch visits so we can pass more data to heap
            fetchCustomerVisits().then((visitResponse) => {
              let visitId;
              let siteId;
              if (visitResponse.success && visitResponse.data!.transactions.length > 0) {
                [{ id: visitId, siteId }] = visitResponse.data!.transactions;
              }
              // failed validation response means the user is logged in already. report to heap
              trackValidationResponse({
                appliedValidationSuccess: false,
                appliedValidationUuid: validation,
                appliedValidationTransactionId: visitId || 'null',
                appliedValidationSiteId: siteId || 'null',
                appliedValidationErrorMessage: response.error,
                isParkingPassValidation: isDiscountPassValidation,
              });
            });
            toaster.create({ title: 'Validation not applied', type: 'error' });
          }
        }

        if (response.success && response.data?.qrDelegation) {
          parseQRDelegation(response.data.qrDelegation);
        } else {
          getUser();
        }
      });
    } else if (gotHereByKioskValidationLink) {
      KioskValidationService.registerAndApplyValidation({ invitationSecret: code })
        .then((response) => {
          if (response.qrDelegation) {
            parseQRDelegation(response.qrDelegation);
          } else {
            getUser();
          }
          if (response.validationId) {
            setQRValidation(String(response.validationId));
          }
          setSignInLandingPage(response.visitId ? '/visit' : '/');
        })
        .catch(() => {
          toaster.create({
            title: 'This link has expired. Please return to the kiosk for a new link.',
            type: 'error',
          });
          getUser();
        });
    } else if (gotHereByEnterpriseInvitationOnboardingToken) {
      getUserandSetEnterpriseInfo();
    } else if (gotHereByIndividualSubscriptionInvitationLink) {
      setPromptUserSignIn(true);
      setIndividualSubscriptionInvitationId(subInviteId);
      setSignInLandingPage('/membership');

      fetchUserAndRedirect(DefaultOnboardingRoutes.SignIn);
    } else {
      if (pathname.match(/^\/membership\/?$/)) {
        setSignInLandingPage('/membership');
      } else if (vehicleSettingsInitialPathCheck(pathname)) {
        setSignInLandingPage(pathname);
      } else if (pathname.match(/^\/claim\/[^/]+/)) {
        const shortCode = pathname.split('/')[2];
        if (shortCode?.toUpperCase() === TN_SMS_DISCOUNT_CODE) {
          setFromSms(true);
          setSignInLandingPage('/visit');
        } else {
          setSignInLandingPage('/promo');
          setPromoShortCode(shortCode);
        }
      } else if (pathname.match(/^\/promo\/?$/) && code) {
        // User landed on /promo?code=X — after login send them to /visit if open visit, else back here
        if (code.toUpperCase() === TN_SMS_DISCOUNT_CODE) {
          setFromSms(true);
          setSignInLandingPage('/visit');
        } else {
          const queryParams = new URLSearchParams();
          queryParams.append('code', code);
          if (utm_source) queryParams.append('utm_source', utm_source);
          if (utm_campaign) queryParams.append('utm_campaign', utm_campaign);
          setSignInLandingPage(`/promo?${queryParams.toString()}`);
        }
      } else if (pathname.match(/^\/promo\/claim/)) {
        // SMS deep link: /promo/claim/code/TN15OFF — short code is in the path, not query string
        const pathCode = pathname.match(/^\/promo\/claim\/code\/([^/]+)/)?.[1] ?? code;
        if (pathCode?.toUpperCase() === TN_SMS_DISCOUNT_CODE) {
          setFromSms(true);
          setSignInLandingPage('/visit');
        } else {
          const queryParams = new URLSearchParams();
          if (pathCode) {
            queryParams.append('code', pathCode);
          }
          if (utm_source) queryParams.append('utm_source', utm_source);
          if (utm_campaign) queryParams.append('utm_campaign', utm_campaign);
          const landingPath = queryParams.toString()
            ? `/promo?${queryParams.toString()}`
            : '/promo';
          setSignInLandingPage(landingPath);
        }
      } else if ((path as string)?.match(/^\/park/)) {
        setSignInLandingPage('/membership');
      } else if ((path as string)?.match(/^\/welcome/)) {
        setPromptUserSignIn(true);
        setSignInLandingPage('/');
      } else if ((path as string)?.match(/^\/v\/[a-zA-Z0-9]+/)) {
        const visitCode = (path as string).split('/')[2];
        setSignInLandingPage(`/visit/${visitCode}`);
      } else if (pathname.match(/^\/visit\/[a-zA-Z0-9]+/)) {
        setSignInLandingPage(pathname);
      } else if (pathname.match(/^\/receipt\/[0-9A-Za-z-]+$/)) {
        setSignInLandingPage(pathname);
      } else if ((path as string)?.match(/^\/pass/)) {
        setSignInLandingPage(ProtectedRoutes.ForwardSlash);
        setSignInLandingPageState({ isPurchasingPass: true });
      } else if ((path as string)?.match(/^\/receipt\/[a-zA-Z0-9]+/)) {
        const invoiceUuid = (path as string).split('/')[2];
        setSignInLandingPage(`/receipt/${invoiceUuid}`);
      }
      // No invitation passed. Check cookies
      fetchUserAndRedirect(() => {
        if (pathname === ProtectedRoutes.ForwardSlash || pathname === ProtectedRoutes.ParkNowPage) {
          history.replace(ProtectedRoutes.ParkNowPage);
        } else {
          history.replace(DefaultOnboardingRoutes.SignIn);
        }
      });
    }

    // Checks local storage for the stored validation
    async function getStoredValidation() {
      const storedVal = lStorage.getItem('mp-validation');
      if (storedVal) {
        const { id: validationId, expire: validationExpire } = JSON.parse(storedVal);
        if (Date.now() < validationExpire) {
          setQRValidation(validationId);
          setSignInLandingPage('/');
        } else {
          lStorage.removeItem('mp-validation');
        }
      }
    }

    // Set validation data
    if (gotHereByValidationQRCode) {
      setQRValidation(validation);
      // Store the validation in case the user has not created an account
      const expire = add(new Date(), { days: 1 }).getTime();
      lStorage.setItem('mp-validation', JSON.stringify({ id: validation, expire }));
      setSignInLandingPage('/');
    } else {
      getStoredValidation();
    }

    // onmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
